import React, { useState, useEffect } from 'react';

import EmailSettings from './EmailSettings';

interface SystemManagementProps {
  token: string;
}

const SystemManagement: React.FC<SystemManagementProps> = ({ token }) => {
  const [backups, setBackups] = useState<any[]>([]);
  const [logs, setLogs] = useState('');
  const [activeTab, setActiveTab] = useState('backups');
  const [systemStatus, setSystemStatus] = useState<any>({});
  const [loading, setLoading] = useState(false);
  const [backupSettings, setBackupSettings] = useState<any>({});
  const [savingBackup, setSavingBackup] = useState(false);
  const [backupMsg, setBackupMsg] = useState('');

  const loadBackupSettings = async () => {
    try {
      const res = await fetch('/api/admin/backup-settings', {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) setBackupSettings(await res.json());
    } catch {}
  };

  const loadBackups = async () => {
    try {
      const res = await fetch('/api/admin/backups', {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      setBackups(data);
    } catch (err) {
      console.error('Failed to load backups:', err);
    }
  };

  const loadHealth = async () => {
    try {
      const res = await fetch('/api/admin/health', {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      setSystemStatus(data);
    } catch (err) {
      console.error('Health check failed:', err);
    }
  };

  const createBackup = async () => {
    setLoading(true);
    try {
      const res = await fetch('/api/admin/backup', {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      if (data.success) {
        alert('Backup created successfully!');
        loadBackups();
      } else {
        alert('Backup failed: ' + data.error);
      }
    } catch (err) {
      alert('Error creating backup');
    }
    setLoading(false);
  };

  const deleteBackup = async (key: string, name: string) => {
    if (!window.confirm(`Delete ${name} permanently?`)) return;
    try {
      const res = await fetch(`/api/admin/backups/${encodeURIComponent(key)}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${localStorage.getItem("token")}` }
      });
      if (res.ok) { loadBackups(); } else { const d = await res.json(); alert('Delete failed: ' + d.error); }
    } catch { alert('Error deleting backup'); }
  };

  const restoreBackup = async (backupName: string) => {
    if (!window.confirm(`Restore ${backupName}? This will overwrite current data.`)) return;
    setLoading(true);
    try {
      const res = await fetch(`/api/admin/restore/${backupName}`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      alert(data.message);
    } catch (err) {
      alert('Restore failed');
    }
    setLoading(false);
  };

  const viewLogs = async (service: string) => {
    setLoading(true);
    try {
      const res = await fetch(`/api/admin/logs/${service}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      setLogs(data.logs || 'No logs available');
    } catch (err) {
      setLogs('Failed to load logs');
    }
    setLoading(false);
  };

  const restartService = async (service: string) => {
    if (!window.confirm(`Restart ${service}?`)) return;
    setLoading(true);
    try {
      const res = await fetch(`/api/admin/service/restart/${service}`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      alert(data.message);
      loadHealth();
    } catch (err) {
      alert('Failed to restart service');
    }
    setLoading(false);
  };

  useEffect(() => {
    loadBackups();
    loadHealth();
    loadBackupSettings();
  }, []);

  return (
    <div>
      <h1 style={{ marginBottom: '20px' }}>System Management</h1>
      
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '20px', marginBottom: '24px' }}>
        <div style={{ background: 'white', padding: '20px', borderRadius: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
          <div style={{ fontSize: '32px', fontWeight: 'bold', color: '#2a5298' }}>{systemStatus.database ? '✅' : '❌'}</div>
          <div>Database</div>
        </div>
        <div style={{ background: 'white', padding: '20px', borderRadius: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
          <div style={{ fontSize: '14px', fontWeight: 'bold', color: '#2a5298' }}>{systemStatus.uptime || '...'}</div>
          <div>Uptime</div>
        </div>
        <div style={{ background: 'white', padding: '20px', borderRadius: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
          <div style={{ fontSize: '14px', fontWeight: 'bold', color: '#2a5298' }}>{systemStatus.memory || '...'}</div>
          <div>Memory</div>
        </div>
        <div style={{ background: 'white', padding: '20px', borderRadius: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
          <div style={{ fontSize: '14px', fontWeight: 'bold', color: '#2a5298' }}>{systemStatus.disk || '...'}</div>
          <div>Disk</div>
        </div>
      </div>

      <div style={{ background: 'white', borderBottom: '1px solid #ddd', padding: '0 16px', display: 'flex', gap: '24px', marginBottom: '20px', overflowX: 'auto' }}>
        <button onClick={() => setActiveTab('backups')} style={{ padding: '12px 0', background: 'none', border: 'none', cursor: 'pointer', fontWeight: activeTab === 'backups' ? 'bold' : 'normal', borderBottom: activeTab === 'backups' ? '2px solid #2a5298' : 'none' }}>Backups</button>
        <button onClick={() => setActiveTab('logs')} style={{ padding: '12px 0', background: 'none', border: 'none', cursor: 'pointer', fontWeight: activeTab === 'logs' ? 'bold' : 'normal', borderBottom: activeTab === 'logs' ? '2px solid #2a5298' : 'none' }}>Logs</button>
        <button onClick={() => setActiveTab('health')} style={{ padding: '12px 0', background: 'none', border: 'none', cursor: 'pointer', fontWeight: activeTab === 'health' ? 'bold' : 'normal', borderBottom: activeTab === 'health' ? '2px solid #2a5298' : 'none' }}>Health</button>
        <button onClick={() => setActiveTab('email')} style={{ padding: '12px 0', background: 'none', border: 'none', cursor: 'pointer', fontWeight: activeTab === 'email' ? 'bold' : 'normal', borderBottom: activeTab === 'email' ? '2px solid #2a5298' : 'none' }}>Email</button>
        <button onClick={() => setActiveTab('services')} style={{ padding: '12px 0', background: 'none', border: 'none', cursor: 'pointer', fontWeight: activeTab === 'services' ? 'bold' : 'normal', borderBottom: activeTab === 'services' ? '2px solid #2a5298' : 'none' }}>Services</button>
      </div>

      {activeTab === 'backups' && (
        <div>
          {/* Backup Config */}
          <div style={{ background: 'white', borderRadius: '12px', padding: '20px', marginBottom: '20px' }}>
            <h3 style={{ margin: '0 0 16px 0', fontSize: '15px', color: '#0f172a' }}>Backup Configuration</h3>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
              <div>
                <label style={{ fontSize: '13px', fontWeight: 500, color: '#475569', display: 'block', marginBottom: '4px' }}>Schedule (cron)</label>
                <input value={backupSettings.schedule || '0 2 * * *'} onChange={e => setBackupSettings({...backupSettings, schedule: e.target.value})}
                  style={{ width: '100%', padding: '8px 12px', border: '1px solid #e2e8f0', borderRadius: '6px', fontSize: '13px', boxSizing: 'border-box' }} />
                <div style={{ fontSize: '11px', color: '#94a3b8', marginTop: '4px' }}>Current: daily at 2AM (0 2 * * *)</div>
              </div>
              <div>
                <label style={{ fontSize: '13px', fontWeight: 500, color: '#475569', display: 'block', marginBottom: '4px' }}>Retention (days)</label>
                <input type="number" value={backupSettings.retention_days || 30} onChange={e => setBackupSettings({...backupSettings, retention_days: parseInt(e.target.value) || 30})}
                  style={{ width: '100%', padding: '8px 12px', border: '1px solid #e2e8f0', borderRadius: '6px', fontSize: '13px', boxSizing: 'border-box' }} />
              </div>
            </div>
            <div style={{ display: 'flex', gap: '16px', marginTop: '12px' }}>
              <label style={{ fontSize: '13px', color: '#475569', display: 'flex', alignItems: 'center', gap: '6px' }}>
                <input type="checkbox" checked={backupSettings.include_db !== false} onChange={e => setBackupSettings({...backupSettings, include_db: e.target.checked})} /> Database
              </label>
              <label style={{ fontSize: '13px', color: '#475569', display: 'flex', alignItems: 'center', gap: '6px' }}>
                <input type="checkbox" checked={backupSettings.include_files !== false} onChange={e => setBackupSettings({...backupSettings, include_files: e.target.checked})} /> Code Files
              </label>
              <label style={{ fontSize: '13px', color: '#475569', display: 'flex', alignItems: 'center', gap: '6px' }}>
                <input type="checkbox" checked={backupSettings.include_uploads !== false} onChange={e => setBackupSettings({...backupSettings, include_uploads: e.target.checked})} /> Uploads
              </label>
            </div>
            <button onClick={async () => {
              setSavingBackup(true); setBackupMsg('');
              try {
                const res = await fetch('/api/admin/backup-settings', {
                  method: 'PUT',
                  headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                  body: JSON.stringify({
                    backup_schedule: backupSettings.schedule,
                    backup_retention_days: backupSettings.retention_days,
                    backup_include_db: backupSettings.include_db,
                    backup_include_files: backupSettings.include_files,
                    backup_include_uploads: backupSettings.include_uploads
                  })
                });
                setBackupMsg(res.ok ? 'Settings saved' : 'Failed to save');
              } catch { setBackupMsg('Error saving'); }
              setSavingBackup(false);
              setTimeout(() => setBackupMsg(''), 3000);
            }} disabled={savingBackup}
              style={{ marginTop: '12px', padding: '8px 16px', background: '#533afd', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '13px' }}>
              {savingBackup ? 'Saving...' : 'Save Backup Settings'}
            </button>
            {backupMsg && <div style={{ marginTop: '8px', padding: '8px', borderRadius: '6px', background: backupMsg.includes('Saved') ? '#d1fae5' : '#fee2e2', color: backupMsg.includes('Saved') ? '#065f46' : '#991b1b', fontSize: '12px' }}>{backupMsg}</div>}
            <div style={{ marginTop: '12px', padding: '10px', background: '#fffbeb', borderRadius: '8px', fontSize: '12px', color: '#92400e' }}>
              ⚠️ Changing the schedule requires updating the server cron job separately. Contact support@solvtek.co.uk to apply schedule changes.
            </div>
          </div>
          {/* Backup List */}
          <div style={{ background: 'white', borderRadius: '12px', padding: '20px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '20px', flexWrap: 'wrap', gap: '12px' }}>
              <h3 style={{ margin: 0, fontSize: '15px', color: '#0f172a' }}>Backup History ({backups.length})</h3>
              <button onClick={createBackup} disabled={loading} style={{ padding: '10px 20px', background: '#2a5298', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer' }}>+ Create Backup</button>
            </div>
            {backups.length === 0 ? (
              <p>No backups found. Click "Create Backup" to create one.</p>
            ) : (
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ background: '#f0f4f8', borderBottom: '2px solid #ddd' }}>
                    <th style={{ padding: '12px', textAlign: 'left' }}>Name</th>
                    <th style={{ padding: '12px', textAlign: 'left' }}>Date</th>
                    <th style={{ padding: '12px', textAlign: 'left' }}>Size</th>
                    <th style={{ padding: '12px', textAlign: 'left' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {backups.map((b, idx) => (
                    <tr key={idx} style={{ borderBottom: '1px solid #eee' }}>
                      <td style={{ padding: '12px' }}>{b.name}</td>
                      <td style={{ padding: '12px' }}>{new Date(b.date).toLocaleString()}</td>
                      <td style={{ padding: '12px' }}>{b.size}</td>
                      <td style={{ padding: '12px' }}>
                        <button onClick={() => restoreBackup(b.name)} disabled={loading} style={{ padding: '6px 12px', background: '#f59e0b', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', marginRight: '8px' }}>Restore</button>
                        <button onClick={() => deleteBackup(b.key, b.name)} disabled={loading} style={{ padding: '6px 12px', background: '#ef4444', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer' }}>Delete</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      )}

      {activeTab === 'logs' && (
        <div style={{ background: 'white', borderRadius: '12px', padding: '20px' }}>
          <h2>System Logs</h2>
          <div style={{ display: 'flex', gap: '12px', marginBottom: '16px', flexWrap: 'wrap' }}>
            <button onClick={() => viewLogs('backend')} disabled={loading} style={{ padding: '8px 16px', background: '#2a5298', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer' }}>Backend</button>
            <button onClick={() => viewLogs('frontend')} disabled={loading} style={{ padding: '8px 16px', background: '#2a5298', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer' }}>Frontend</button>
            <button onClick={() => viewLogs('database')} disabled={loading} style={{ padding: '8px 16px', background: '#2a5298', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer' }}>Database</button>
          </div>
          <pre style={{ background: '#1e1e1e', color: '#d4d4d4', padding: '16px', borderRadius: '8px', overflow: 'auto', maxHeight: '400px', fontSize: '12px' }}>{logs || 'Click a button to view logs...'}</pre>
        </div>
      )}

      {activeTab === 'health' && (
        <div style={{ background: 'white', borderRadius: '12px', padding: '20px' }}>
          <h2 style={{ margin: '0 0 16px 0' }}>Server Health</h2>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
            <div style={{ padding: '12px', background: '#f8fafc', borderRadius: '8px' }}>
              <div style={{ fontSize: '12px', color: '#94a3b8' }}>Status</div>
              <div style={{ fontSize: '16px', fontWeight: 600, color: systemStatus.status === 'healthy' ? '#059669' : '#dc2626' }}>
                {systemStatus.status === 'healthy' ? '✅ Healthy' : '❌ Degraded'}
              </div>
            </div>
            <div style={{ padding: '12px', background: '#f8fafc', borderRadius: '8px' }}>
              <div style={{ fontSize: '12px', color: '#94a3b8' }}>Database</div>
              <div style={{ fontSize: '16px', fontWeight: 600, color: systemStatus.database ? '#059669' : '#dc2626' }}>
                {systemStatus.database ? '✅ Connected' : '❌ Disconnected'}
              </div>
            </div>
            <div style={{ padding: '12px', background: '#f8fafc', borderRadius: '8px' }}>
              <div style={{ fontSize: '12px', color: '#94a3b8' }}>Uptime</div>
              <div style={{ fontSize: '14px', fontWeight: 600, color: '#0f172a' }}>{systemStatus.uptime}</div>
            </div>
            <div style={{ padding: '12px', background: '#f8fafc', borderRadius: '8px' }}>
              <div style={{ fontSize: '12px', color: '#94a3b8' }}>CPU Load</div>
              <div style={{ fontSize: '14px', fontWeight: 600, color: '#0f172a' }}>{systemStatus.load}</div>
            </div>
            <div style={{ padding: '12px', background: '#f8fafc', borderRadius: '8px' }}>
              <div style={{ fontSize: '12px', color: '#94a3b8' }}>Memory</div>
              <div style={{ fontSize: '14px', fontWeight: 600, color: '#0f172a' }}>{systemStatus.memory}</div>
            </div>
            <div style={{ padding: '12px', background: '#f8fafc', borderRadius: '8px' }}>
              <div style={{ fontSize: '12px', color: '#94a3b8' }}>Swap</div>
              <div style={{ fontSize: '14px', fontWeight: 600, color: '#0f172a' }}>{systemStatus.swap}</div>
            </div>
            <div style={{ padding: '12px', background: '#f8fafc', borderRadius: '8px' }}>
              <div style={{ fontSize: '12px', color: '#94a3b8' }}>Disk</div>
              <div style={{ fontSize: '14px', fontWeight: 600, color: '#0f172a' }}>{systemStatus.disk}</div>
            </div>
            <div style={{ padding: '12px', background: '#f8fafc', borderRadius: '8px' }}>
              <div style={{ fontSize: '12px', color: '#94a3b8' }}>Processes</div>
              <div style={{ fontSize: '14px', fontWeight: 600, color: '#0f172a' }}>{systemStatus.processes}</div>
            </div>
            <div style={{ padding: '12px', background: '#f8fafc', borderRadius: '8px' }}>
              <div style={{ fontSize: '12px', color: '#94a3b8' }}>Last Backup</div>
              <div style={{ fontSize: '14px', fontWeight: 600, color: '#0f172a' }}>{systemStatus.lastBackup ? new Date(systemStatus.lastBackup).toLocaleString('en-GB') : 'Never'}</div>
            </div>
            <div style={{ padding: '12px', background: '#f8fafc', borderRadius: '8px' }}>
              <div style={{ fontSize: '12px', color: '#94a3b8' }}>Docker</div>
              <div style={{ fontSize: '13px', fontWeight: 500, color: '#0f172a', wordBreak: 'break-all' }}>{systemStatus.docker}</div>
            </div>
          </div>
          <button onClick={loadHealth} disabled={loading} style={{ marginTop: '16px', padding: '10px 20px', background: '#2a5298', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer' }}>🔄 Refresh</button>
        </div>
      )}

      {activeTab === 'email' && (
        <EmailSettings token={token} />
      )}
      {activeTab === 'services' && (
        <div style={{ background: 'white', borderRadius: '12px', padding: '20px' }}>
          <h2>Service Management</h2>
          <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
            <button onClick={() => restartService('all')} disabled={loading} style={{ padding: '10px 20px', background: '#dc2626', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer' }}>Restart All</button>
            <button onClick={() => restartService('backend')} disabled={loading} style={{ padding: '10px 20px', background: '#f59e0b', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer' }}>Restart Backend</button>
            <button onClick={() => restartService('frontend')} disabled={loading} style={{ padding: '10px 20px', background: '#f59e0b', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer' }}>Restart Frontend</button>
            <button onClick={() => restartService('db')} disabled={loading} style={{ padding: '10px 20px', background: '#f59e0b', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer' }}>Restart Database</button>
          </div>
          <p style={{ marginTop: '16px', color: '#666', fontSize: '12px' }}>Note: Restarting services may cause temporary downtime.</p>
        </div>
      )}
    </div>
  );
};

export default SystemManagement;
